import { LoyaltyApi } from "@smartrr/shared/interfaces/loyalty/api";
import { useVariantIdToVariantMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import React, { useMemo } from "react";

import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";
import { usePurchaseStates } from "../../../../../hooks/usePurchaseStates";
import { RedeemVariantDelivery } from "./RedeemVariantDelivery";
import { RedeemVariantOTP } from "./RedeemVariantOTP";
import { LoyaltyStoreAccess } from "../../../store";
import { getNearestUnpauseDate } from "../../../utils";
import { RedeemVariantUnpause } from "./RedeemVariantUnpause";
import { formatProductAndVariantNameForDisplay } from "@smartrr/shared/utils/displayUtils";
import { useSmartrrFlags } from "@smartrr/shared/LaunchDarkly";

export interface RedeemVariantModalProps {
  variant: LoyaltyApi.RewardProduct.Type;
}

export const RedeemVariantModal = ({ variant }: RedeemVariantModalProps) => {
  const upcomingOrder = LoyaltyStoreAccess.useOrder();
  const purchaseStates = usePurchaseStates();
  const flags = useSmartrrFlags();

  const purchasables = useSmartrrCustomerSelector(state => state.purchasables.purchasables);
  const variantMap = useVariantIdToVariantMap(purchasables);

  const closeModal = useCloseModal();

  const purchasableName = formatProductAndVariantNameForDisplay({
    purchasableName: variant.variant.productName,
    purchasableVariantName: variant.variant.variantName,
  });
  const allInactive = useMemo(
    () =>
      purchaseStates.every(
        purchaseState =>
          purchaseState.purchaseStateStatus === "PAUSED" || purchaseState.purchaseStateStatus === "CANCELLED"
      ),
    [purchaseStates]
  );
  const pausedPurchaseStates = useMemo(
    () => purchaseStates.filter(purchaseState => purchaseState.purchaseStateStatus === "PAUSED"),
    [purchaseStates]
  );

  const nearestPurchaseStateWithUnpauseDate = getNearestUnpauseDate(pausedPurchaseStates);
  if (allInactive && nearestPurchaseStateWithUnpauseDate) {
    return (
      <RedeemVariantUnpause
        variant={variant}
        purchasableName={purchasableName}
        shopifyVariant={variantMap[variant.variantId]}
        closeVariantModal={closeModal}
        purchaseState={nearestPurchaseStateWithUnpauseDate}
      />
    );
  }

  if (upcomingOrder) {
    return (
      <RedeemVariantDelivery
        upcomingOrder={upcomingOrder}
        variant={variant}
        purchasableName={purchasableName}
        closeVariantModal={closeModal}
        shopifyVariant={variantMap[variant.variantId]}
      />
    );
  }

  return (
    <RedeemVariantOTP
      variant={variant}
      closeVariantModal={closeModal}
      shopifyVariant={variantMap[variant.variantId]}
    />
  );
};
